import { useCallback, useState, useMemo, useContext } from 'react';
import { getLoggedInUser } from '../../utils/jose';
import { ChangePasswordProvider } from '../../contexts/changePassword';
import { CommonPortalContext } from "../../contexts/commonPortal";
import { IPopup } from '../../constants/commonPortal.constant';
import { POPUP_TYPE } from '../../components/subComponents/commonModal';

const defaultField = () => ({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const defaultError = () => ({
  has: true,
  reason: '',
  message: '',
});

const defaultVisibility = () => ({
  currentPassword: false,
  newPassword: false,
  confirmPassword: false,
});

/**
 * 
 * @param {{
 *    component: React.JSX.Element;
 * }} props 
 * @returns {React.JSX.Element}
 */
const ChangePasswordController = (props) => {
  const {
    component: Component,
    ChangePassword: changePassword,
    changePasswordAPI,
  } = props;

  const { handler: { handleSnackbarClose, handleSnackbarOpen } } = useContext(CommonPortalContext);

  const [field, setField] = useState(defaultField());

  const [visibility, setVisibility] = useState(defaultVisibility());

  const [error, setError] = useState(defaultError());

  const rulesValidation = useMemo(() => {
    const AllowedCharactersInPassword = /[^a-zA-Z0-9!@#$%^*-]/gm;
    const RequiredNumbers = /[0-9]/gm;
    const RequiredUppercase = /[A-Z]/gm;
    const RequiredLowercase = /[a-z]/gm;
    const RequiredSymbols = /[!@#$%^*-]/gm;
    /**
     * @type string
     */
    const current = field?.currentPassword || '';
    /**
     * @type string
     */
    const password = field?.newPassword || '';
    /**
     * @type string
     */
    const confirm = field?.confirmPassword || '';

    /**
     * @type {Map<string, boolean>}
     */
    const collection = new Map([
      ['current-password', true],
      ['8-characters', true],
      ['1-number', true],
      ['1-uppercase', true],
      ['1-lowercase', true],
      ['1-special-character', true],
      ['passwords-matched', true],
    ]);

    if (current?.trim()?.length < 8) {
      collection.set('current-password', false);
    }

    if (password?.trim()?.length < 8) {
      collection.set('8-characters', false);
    }

    if (!RequiredSymbols.test(password)) {
      collection.set('1-special-character', false);
    }

    if (AllowedCharactersInPassword.test(password)) {
      collection.set('1-special-character', false);
    }

    if (!RequiredNumbers.test(password)) {
      collection.set('1-number', false);
    }

    if (!RequiredUppercase.test(password)) {
      collection.set('1-uppercase', false);
    }

    if (!RequiredLowercase.test(password)) {
      collection.set('1-lowercase', false);
    }

    if (confirm?.trim()?.length < 8) {
      collection.set('passwords-matched', false);
    }

    if (confirm !== password) {
      collection.set('passwords-matched', false);
    }
    return collection;
  }, [field]);

  const manageValidationError = useCallback((__field) => {
    const AllowedCharactersInPassword = /[^a-zA-Z0-9!@#$%^*-]/gm;
    const RequiredNumbers = /[0-9]/gm;
    const RequiredUppercase = /[A-Z]/gm;
    const RequiredLowercase = /[a-z]/gm;
    const RequiredSymbols = /[!@#$%^*-]/gm;

    /**
     * @type string
     */
    const current = __field?.currentPassword || '';
    /**
     * @type string
     */
    const password = __field?.newPassword || '';
    /**
     * @type string
     */
    const confirm = __field?.confirmPassword || '';

    if (current?.trim()?.length < 8) {
      return setError({
        has: true,
        reason: `current: ${current}`,
        message: 'At least 8 characters.',
      });
    }

    if (AllowedCharactersInPassword.test(password)) {
      return setError({
        has: true,
        reason: `password: ${password}`,
        message: 'Only !@#$%^*- characters are allowed in password.',
      });
    }

    if (!RequiredNumbers.test(password)) {
      return setError({
        has: true,
        reason: `password: ${password}`,
        message: 'At least 1 number.',
      });
    }

    if (!RequiredUppercase.test(password)) {
      return setError({
        has: true,
        reason: `password: ${password}`,
        message: 'At least 1 uppercase letter.',
      });
    }

    if (!RequiredLowercase.test(password)) {
      return setError({
        has: true,
        reason: `password: ${password}`,
        message: 'At least 1 lowercase letter.',
      });
    }

    if (!RequiredSymbols.test(password)) {
      return setError({
        has: true,
        reason: `password: ${password}`,
        message: 'At least 1 of the mentioned special characters.',
      });
    }

    if (password.length < 8) {
      return setError({
        has: true,
        reason: `password: ${password}`,
        message: 'At least 8 characters.',
      });
    }

    if (confirm !== password) {
      return setError({
        has: true,
        reason: `confirm: ${confirm}`,
        message: 'New password & confirm password must match.',
      });
    }

    return setError({
      has: false,
      reason: '',
      message: '',
    });
  }, [error, field]);

  const handleChange = useCallback((event) => {
    const value = event?.target?.value || '';
    const name = event?.target?.name || '';
    const __field = { ...field };
    if (name) {
      __field[name] = value;
    }
    setField(__field);
    manageValidationError(__field);
  }, [field, manageValidationError]);

  const handleChangeVisibility = useCallback((event) => {
    const value = event?.target?.value || false;
    const name = event?.target?.name || '';
    const __visibility = { ...visibility };
    if (name) {
      __visibility[name] = value;
    }
    setVisibility(__visibility);
  }, [visibility]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    try {
      const peopleId = getLoggedInUser().UserId;
      const { status } = await changePassword({
        peopleId,
        oldPassword: field.currentPassword,
        newPassword: field.newPassword,
      });
      if (status === 200) {
        handleSnackbarOpen(IPopup({
          open: true,
          title: 'Successful',
          type: POPUP_TYPE.SUCCESS,
          message: 'Password has been updated successfully.',
          onOk: handleSnackbarClose,
        }));
        setField(defaultField());
        setVisibility(defaultVisibility());
        setError(defaultError());
      }

      if (status !== 200) {
        throw Error('');
      }
    } catch (e) {
      handleSnackbarOpen(IPopup({
        open: true,
        title: 'Error.',
        type: POPUP_TYPE.ERROR,
        message: 'Please check the information entered and try again.',
        onOk: handleSnackbarClose,
      }));
    }
  }, [field]);

  return (
    <ChangePasswordProvider
      value={{
        field,
        visibility,
        error,
        rulesValidation,
        changePasswordAPI,
        handleChange,
        handleChangeVisibility,
        handleSubmit,
      }}
    >
      <Component {...props} />
    </ChangePasswordProvider>
  );
};

/**
 * @param {React.JSX.Element} Component 
 * @returns {(props: object) => TasksController}
 */
export const ChangePasswordControllerHOC = (Component) => (props) => (
  <ChangePasswordController component={Component} {...props} />
);

export default ChangePasswordControllerHOC;
